"use client"

import { Button } from '../../../shared/components/ui/button'
import { SlidersHorizontal } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useIsMobile } from '../../../shared/hooks/use-mobile'
import { FormProvider, useForm } from 'react-hook-form'
import { roleFilterOptions, statusFilterWithAllOptions } from '../constants/options'
import { usePathname, useRouter } from 'next/navigation'
import { buildUrl } from '../../../shared/lib/utils'
import { DASHBOARD_ROUTE } from '../../../shared/constants/pages-routes'
import { Dialog, DialogTitle, DialogContent, DialogTrigger, DialogClose } from '../../../shared/components/ui/dialog'
import { useCurrentSearchParams } from '../../../shared/hooks/use-current-search-params'
import { VolunteerQueryParamsType, volunteerQueryParamsWithoutPaginationSchema } from '../schemas/volunteer-query.schema'
import { DialogDescription } from '@radix-ui/react-dialog'
import FormMultiSelectInput from '../../../shared/components/form-inputs/form-multi-select-input'
import FormDateInput from '../../../shared/components/form-inputs/form-date-input'
import FormOrganizationInput from '../../../shared/components/form-inputs/form-organization-input'
import FormSelectInput from '../../../shared/components/form-inputs/form-select-input'
import { useQueryClient } from '@tanstack/react-query'
import { ADMIN } from '@/shared/constants/backend-api'
import { SEARCH_PARAMS_TAB_KEY } from '../constants/volunteer-tab-links'

type VolunteerFilterSearchParams = VolunteerQueryParamsType & {
    [SEARCH_PARAMS_TAB_KEY]?: string | null
}

type VolunteerFilterFormValues = Omit<VolunteerQueryParamsType, 'arrivalDate' | 'departureDate' | 'addedFrom' | 'addedTo' | 'lastActiveAtFrom' | 'lastActiveAtTo' | 'status'> & {
    arrivalDate?: Date | string
    departureDate?: Date | string
    addedFrom?: Date | string
    addedTo?: Date | string
    lastActiveAtFrom?: Date | string
    lastActiveAtTo?: Date | string
    status?: string // 'all' | 'active' | 'inactive'
}

function parseDateForForm(dateStr: string | null | undefined): Date | undefined {
    if (!dateStr) return undefined
    try {
        const str = dateStr.split('T')[0]
        if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return undefined
        const [year, month, day] = str.split('-').map(Number)
        return new Date(year, month - 1, day)
    } catch {
        return undefined
    }
}

function dateToYyyyMmDd(val: Date | string | undefined): string | undefined {
    if (!val) return undefined
    if (val instanceof Date) return val.toISOString().split('T')[0]
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10)
    return undefined
}

function VolunteerFilterButton() {
    const isMobile = useIsMobile()
    const router = useRouter()
    const pathname = usePathname()
    const queryClient = useQueryClient()
    const previousParams = useCurrentSearchParams<VolunteerFilterSearchParams>([
        "limit",
        SEARCH_PARAMS_TAB_KEY,
        "roles",
        "arrivalDate",
        "departureDate",
        "organization",
        "addedFrom",
        "addedTo",
        "lastActiveAtFrom",
        "lastActiveAtTo",
        "status",
    ])

    // buildUrl expects undefined (not null) for omitted values
    const sanitizedPreviousParams = useMemo(() => previousParams && Object.fromEntries(
        Object.entries(previousParams).map(([k, v]) => [k, v === null ? undefined : v])
    ), [previousParams])

    const normalizedParams = {
        ...previousParams,
        roles: Array.isArray(previousParams.roles)
            ? previousParams.roles
            : previousParams.roles
                ? [previousParams.roles]
                : []
    }

    const parsedPreviousParams = volunteerQueryParamsWithoutPaginationSchema.safeParse(normalizedParams)
    const parsed = parsedPreviousParams.success ? parsedPreviousParams.data : null
    const roles = parsed?.roles || []
    const arrivalDate = parsed?.arrivalDate
    const departureDate = parsed?.departureDate
    const organization = parsed?.organization
    const addedFrom = parsed?.addedFrom
    const addedTo = parsed?.addedTo
    const lastActiveAtFrom = parsed?.lastActiveAtFrom
    const lastActiveAtTo = parsed?.lastActiveAtTo
    const statusParam = parsed?.status // boolean | undefined from URL

    const [isOpen, setIsOpen] = useState(false)

    const statusForForm = statusParam === true ? 'active' : statusParam === false ? 'inactive' : 'all'

    const form = useForm<VolunteerFilterFormValues>({
        defaultValues: {
            roles,
            arrivalDate: parseDateForForm(arrivalDate),
            departureDate: parseDateForForm(departureDate),
            organization,
            addedFrom: parseDateForForm(addedFrom),
            addedTo: parseDateForForm(addedTo),
            lastActiveAtFrom: parseDateForForm(lastActiveAtFrom),
            lastActiveAtTo: parseDateForForm(lastActiveAtTo),
            status: statusForForm,
        }
    })

    const { isDirty, isSubmitting } = form.formState
    const { control, watch } = form

    const currentRoles = watch('roles') || []
    const currentArrivalDate = watch('arrivalDate')
    const currentDepartureDate = watch('departureDate')
    const currentOrganization = watch('organization')
    const currentAddedFrom = watch('addedFrom')
    const currentAddedTo = watch('addedTo')
    const currentLastActiveAtFrom = watch('lastActiveAtFrom')
    const currentLastActiveAtTo = watch('lastActiveAtTo')
    const currentStatus = watch('status')

    const hasActiveFilters =
        currentRoles.length > 0 ||
        !!currentArrivalDate ||
        !!currentDepartureDate ||
        !!currentOrganization ||
        !!currentAddedFrom ||
        !!currentAddedTo ||
        !!currentLastActiveAtFrom ||
        !!currentLastActiveAtTo ||
        (currentStatus && currentStatus !== 'all')

    const handleSubmit = (data: VolunteerFilterFormValues) => {
        const arrivalDateString = dateToYyyyMmDd(data.arrivalDate)
        const departureDateString = dateToYyyyMmDd(data.departureDate)
        const addedFromString = dateToYyyyMmDd(data.addedFrom)
        const addedToString = dateToYyyyMmDd(data.addedTo)
        const lastActiveAtFromString = dateToYyyyMmDd(data.lastActiveAtFrom)
        const lastActiveAtToString = dateToYyyyMmDd(data.lastActiveAtTo)
        const statusUrl = data.status === 'all' ? undefined : data.status === 'active' ? 'true' : 'false'

        const url = buildUrl(
            pathname || DASHBOARD_ROUTE.volunteers,
            {
                roles: data.roles,
                arrivalDate: arrivalDateString,
                departureDate: departureDateString,
                organization: data.organization,
                addedFrom: addedFromString,
                addedTo: addedToString,
                lastActiveAtFrom: lastActiveAtFromString,
                lastActiveAtTo: lastActiveAtToString,
                status: statusUrl,
            },
            { previousParams: sanitizedPreviousParams }
        )
        router.push(url)

        queryClient.invalidateQueries({ queryKey: [ADMIN.tableRows] })

        setIsOpen(false)
        form.reset(data)
    }

    const handleClearAll = () => {
        const url = buildUrl(
            pathname || DASHBOARD_ROUTE.volunteers,
            {
                roles: undefined,
                arrivalDate: undefined,
                departureDate: undefined,
                organization: undefined,
                addedFrom: undefined,
                addedTo: undefined,
                lastActiveAtFrom: undefined,
                lastActiveAtTo: undefined,
                status: undefined,
            },
            { previousParams: sanitizedPreviousParams }
        )
        router.push(url)

        queryClient.invalidateQueries({ queryKey: [ADMIN.tableRows] })

        form.reset({
            roles: [],
            arrivalDate: undefined,
            departureDate: undefined,
            organization: undefined,
            addedFrom: undefined,
            addedTo: undefined,
            lastActiveAtFrom: undefined,
            lastActiveAtTo: undefined,
            status: 'all',
        })

        setIsOpen(false)
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button
                    variant={"default"}
                    size={"sm"}
                    className='bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10'
                >
                    <SlidersHorizontal className="w-5 h-5" />
                    {!isMobile && <span>Filter</span>}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogTitle>
                    Filters
                </DialogTitle>
                <DialogDescription className='hidden'>
                    Filter volunteers by status and location
                </DialogDescription>
                <FormProvider {...form}>
                    <form
                        onSubmit={form.handleSubmit(handleSubmit)}
                        className='flex flex-col gap-3'
                    >
                        <FormMultiSelectInput
                            name='roles'
                            options={roleFilterOptions}
                            placeholder='Select roles'
                            inputGroupProps={{
                                label: 'Roles',
                            }}
                            control={control}
                        />
                        <FormOrganizationInput
                            name='organization'
                            placeholder='Search or type organization name...'
                            inputGroupProps={{
                                label: 'Organization/Church/Individual',
                            }}
                            control={control}
                        />
                        <FormDateInput
                            name='arrivalDate'
                            placeholder='Select serve date'
                            inputGroupProps={{
                                label: 'Serve Date',
                            }}
                            control={control}
                            enableYearMonthDropdowns={true}
                        />
                        <FormSelectInput
                            name='status'
                            control={control}
                            options={statusFilterWithAllOptions}
                            placeholder='Select status'
                            inputGroupProps={{
                                label: 'Status',
                            }}
                        />
                        <FormDateInput
                            name='departureDate'
                            placeholder='Select departure date'
                            inputGroupProps={{
                                label: 'Departure Date',
                            }}
                            control={control}
                            enableYearMonthDropdowns={true}
                        />
                        <FormDateInput
                            name='addedFrom'
                            placeholder='Select from date'
                            inputGroupProps={{
                                label: 'Added (from)',
                            }}
                            control={control}
                            enableYearMonthDropdowns={true}
                        />
                        <FormDateInput
                            name='addedTo'
                            placeholder='Select to date'
                            inputGroupProps={{
                                label: 'Added (to)',
                            }}
                            control={control}
                            enableYearMonthDropdowns={true}
                        />
                        <FormDateInput
                            name='lastActiveAtFrom'
                            placeholder='Select from date'
                            inputGroupProps={{
                                label: 'Last Active (from)',
                            }}
                            control={control}
                            enableYearMonthDropdowns={true}
                        />
                        <FormDateInput
                            name='lastActiveAtTo'
                            placeholder='Select to date'
                            inputGroupProps={{
                                label: 'Last Active (to)',
                            }}
                            control={control}
                            enableYearMonthDropdowns={true}
                        />
                        <div className='grid grid-cols-2 gap-2 pt-3'>
                            <DialogClose asChild>
                                <Button
                                    variant={"outline"}
                                    className='w-full'
                                    type='button'
                                >
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                type='submit'
                                className='w-full'
                                disabled={isSubmitting || (!isDirty && !hasActiveFilters)}
                                showLoader={isSubmitting}
                            >
                                Apply
                            </Button>
                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}

export default VolunteerFilterButton